import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  Users, Plus, Pencil, Trash2, Mail, MapPin, X, Save, Search,
  Star, Send, AlertTriangle, TrendingUp, Sparkles, Zap
} from 'lucide-react';

const CATEGORIES = [
  { value: 'plomberie', label: 'Plomberie', dot: '#3B82F6', soft: 'bg-blue-50 text-blue-700' },
  { value: 'electricite', label: 'Électricité', dot: '#EAB308', soft: 'bg-yellow-50 text-yellow-700' },
  { value: 'chauffage', label: 'Chauffage', dot: '#F97316', soft: 'bg-orange-50 text-orange-700' },
  { value: 'menage', label: 'Ménage', dot: '#10B981', soft: 'bg-emerald-50 text-emerald-700' },
  { value: 'maintenance', label: 'Maintenance', dot: '#64748B', soft: 'bg-slate-100 text-slate-700' },
  { value: 'serrurerie', label: 'Serrurerie', dot: '#A855F7', soft: 'bg-purple-50 text-purple-700' },
  { value: 'electromenager', label: 'Électroménager', dot: '#06B6D4', soft: 'bg-cyan-50 text-cyan-700' },
  { value: 'autre', label: 'Autre', dot: '#9CA3AF', soft: 'bg-gray-100 text-gray-600' },
];

const AVATAR_PALETTE = [
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-pink-100', text: 'text-pink-700' },
  { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { bg: 'bg-orange-100', text: 'text-orange-700' },
];

const emptyForm = { company_name: '', email: '', city: '', note: '', categories: [] };

// ---------- helpers ----------
function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function colorFromName(name) {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

function relativeDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 7) return `il y a ${days} j`;
  if (days < 30) return `il y a ${Math.floor(days / 7)} sem`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

function getQualityTag(c) {
  if (c.mission_count === 0) {
    return { label: '✨ Nouveau', cls: 'bg-blue-50 text-blue-700' };
  }
  if (!c.is_active_30d) {
    return { label: '💤 Inactif', cls: 'bg-slate-100 text-slate-500' };
  }
  if (c.acceptance_rate >= 85) {
    return { label: '⚡ Réactif', cls: 'bg-emerald-50 text-emerald-700' };
  }
  return null;
}

export const ContractorsPage = () => {
  const api = useApi();
  const [contractors, setContractors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('gt_contractor_favs') || '[]'); } catch { return []; }
  });
  const [sortBy, setSortBy] = useState('alpha');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const [listRes, sumRes] = await Promise.all([
        api.get(`/contractors?${params.toString()}`),
        api.get('/contractors/stats/summary'),
      ]);
      setContractors(listRes.data);
      setSummary(sumRes.data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [api, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem('gt_contractor_favs', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const toggleCategory = (cat) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat)
        ? f.categories.filter(c => c !== cat)
        : [...f.categories, cat]
    }));
  };

  const handleSave = async () => {
    if (!form.company_name.trim() || !form.email.trim()) {
      toast.error('Nom et email requis');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/contractors/${editingId}`, form);
        toast.success('Contact modifié');
      } else {
        await api.post('/contractors', form);
        toast.success('Contact ajouté');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchAll();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c) => {
    setForm({
      company_name: c.company_name,
      email: c.email,
      city: c.city || '',
      note: c.note || '',
      categories: c.categories || [],
    });
    setEditingId(c.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} ?`)) return;
    try {
      await api.delete(`/contractors/${id}`);
      toast.success('Contact supprimé');
      fetchAll();
    } catch {
      toast.error('Erreur');
    }
  };

  // Category counts from current list
  const categoryCounts = useMemo(() => {
    const counts = {};
    contractors.forEach(c => {
      (c.categories || []).forEach(cat => { counts[cat] = (counts[cat] || 0) + 1; });
    });
    return counts;
  }, [contractors]);

  // Filtered + sorted list
  const visible = useMemo(() => {
    let list = filterCategory
      ? contractors.filter(c => c.categories?.includes(filterCategory))
      : contractors;
    if (sortBy === 'active') {
      list = [...list].sort((a, b) => (b.mission_count || 0) - (a.mission_count || 0));
    } else if (sortBy === 'recent') {
      list = [...list].sort((a, b) => new Date(b.last_mission_at || 0) - new Date(a.last_mission_at || 0));
    } else if (sortBy === 'acceptance') {
      list = [...list].sort((a, b) => (b.acceptance_rate || 0) - (a.acceptance_rate || 0));
    } else {
      list = [...list].sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
    }
    // Favorites bubble up
    return [...list].sort((a, b) => {
      const af = favorites.includes(a.id) ? 0 : 1;
      const bf = favorites.includes(b.id) ? 0 : 1;
      return af - bf;
    });
  }, [contractors, filterCategory, sortBy, favorites]);

  const missingCategoriesLabels = useMemo(() => {
    if (!summary?.missing_categories?.length) return [];
    return summary.missing_categories.map(slug => {
      const c = CATEGORIES.find(x => x.value === slug);
      return c?.label || slug;
    });
  }, [summary]);

  return (
    <MainLayout>
      <Header
        title="Carnet de contacts"
        subtitle="Artisans et prestataires pour les missions terrain"
        actions={
          <Button
            onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
            className="bg-[#FF5A6E] hover:bg-[#E84B5F] text-white"
          >
            <Plus size={16} className="mr-2" /> Ajouter un contact
          </Button>
        }
      />

      <div className="p-6 space-y-5 animate-fadeIn">
        {/* Stats summary */}
        {summary && (
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-3 h-3 text-slate-400" />
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total contacts</p>
                </div>
                <p className="text-2xl font-bold text-slate-900">{summary.total}</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute w-full h-full rounded-full bg-emerald-500 opacity-50 animate-ping" />
                    <span className="relative w-full h-full rounded-full bg-emerald-500" />
                  </span>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Actifs ce mois</p>
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {summary.active_30d} <span className="text-sm font-normal text-slate-400">/ {summary.total}</span>
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Catégories couvertes</p>
                <div className="flex items-baseline gap-1">
                  <p className="text-2xl font-bold text-slate-900">{summary.covered_categories.length}</p>
                  <p className="text-sm text-slate-400">/ {summary.total_categories}</p>
                </div>
                {summary.missing_categories.length > 0 && (
                  <p className="text-[10px] text-amber-600 mt-0.5">
                    Manque : {missingCategoriesLabels.slice(0, 2).join(', ')}
                    {missingCategoriesLabels.length > 2 && ` +${missingCategoriesLabels.length - 2}`}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-100 shadow-[0_1px_4px_rgba(0,0,0,0.03)]">
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Taux d'acceptation</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold text-slate-900">
                    {summary.global_acceptance_rate}%
                  </p>
                  {summary.total_missions > 0 && (
                    <span className="text-[11px] text-slate-400">sur {summary.total_missions} mission{summary.total_missions > 1 ? 's' : ''}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Add/Edit form (inline) */}
        {showForm && (
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">
                  {editingId ? 'Modifier le contact' : 'Nouveau contact'}
                </h3>
                <button onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm); }} className="text-slate-400 hover:text-slate-600">
                  <X size={18} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Nom de l'entreprise *</label>
                  <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Ex: Plomberie Martin" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Email *</label>
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="contact@plomberie-martin.fr" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Ville</label>
                  <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Paris" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Note</label>
                  <Input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Disponible weekend, tarif 60€/h..." />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">Catégories</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => toggleCategory(cat.value)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                        form.categories.includes(cat.value)
                          ? 'bg-[#FF5A6E] text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: form.categories.includes(cat.value) ? 'white' : cat.dot }} />
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving} className="bg-[#FF5A6E] hover:bg-[#E84B5F] text-white">
                  <Save size={14} className="mr-2" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm); }}>
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Toolbar : search + sort */}
        <section className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Rechercher un nom, email, ville..."
              className="w-full pl-10 pr-12 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF5A6E]/20 focus:border-[#FF5A6E] focus:bg-white"
            />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600">
            <option value="alpha">Trier : Alphabétique</option>
            <option value="active">Trier : Plus actifs</option>
            <option value="recent">Trier : Récents</option>
            <option value="acceptance">Trier : Taux acceptation</option>
          </select>
        </section>

        {/* Category chips */}
        <section className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setFilterCategory('')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!filterCategory ? 'bg-[#FF5A6E] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
          >
            Toutes <span className={`ml-0.5 ${!filterCategory ? 'opacity-70' : 'text-slate-400'}`}>{contractors.length}</span>
          </button>
          {CATEGORIES.map(cat => {
            const count = categoryCounts[cat.value] || 0;
            const isActive = filterCategory === cat.value;
            const isMissing = count === 0;
            return (
              <button
                key={cat.value}
                onClick={() => setFilterCategory(isActive ? '' : cat.value)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  isActive ? 'bg-[#FF5A6E] text-white'
                  : isMissing ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: isActive ? 'white' : cat.dot }} />
                {cat.label} <span className={`ml-0.5 ${isActive ? 'opacity-70' : (isMissing ? 'text-amber-600' : 'text-slate-400')}`}>{count}</span>
                {isMissing && !isActive && <span className="text-[9px]">⚠</span>}
              </button>
            );
          })}
        </section>

        {/* Contractors list */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.03)] overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              {visible.length} contact{visible.length !== 1 ? 's' : ''}
              {filterCategory && (
                <span className="text-slate-400 font-normal normal-case ml-1">
                  ({CATEGORIES.find(x => x.value === filterCategory)?.label})
                </span>
              )}
            </h2>
          </div>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Chargement...</div>
            ) : visible.length === 0 ? (
              <div className="p-8 text-center">
                <Users size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">
                  {contractors.length === 0
                    ? 'Aucun contact. Ajoutez vos artisans et prestataires.'
                    : 'Aucun résultat pour ces filtres.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visible.map(c => {
                  const palette = colorFromName(c.company_name);
                  const ini = initials(c.company_name);
                  const isFav = favorites.includes(c.id);
                  const isNew = c.mission_count === 0;
                  const quality = getQualityTag(c);
                  return (
                    <div key={c.id} className="group flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors">
                      {/* Avatar with status dot */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-11 h-11 rounded-full ${palette.bg} ${palette.text} flex items-center justify-center font-bold text-sm`}>
                          {ini}
                        </div>
                        {!isNew && (
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                              c.is_active_30d ? 'bg-emerald-500' : 'bg-slate-300'
                            }`}
                            title={c.is_active_30d ? 'Actif récemment' : 'Inactif'}
                          />
                        )}
                      </div>

                      {/* Main content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <button
                            onClick={() => toggleFavorite(c.id)}
                            className={`transition-all hover:scale-110 ${isFav ? 'text-amber-400' : 'text-slate-300 hover:text-amber-400'}`}
                            title={isFav ? 'Retirer des favoris' : 'Marquer favori'}
                          >
                            <Star size={14} fill={isFav ? 'currentColor' : 'none'} />
                          </button>
                          <p className={`font-semibold ${c.is_active_30d || isNew ? 'text-slate-900' : 'text-slate-700'} truncate`}>
                            {c.company_name}
                          </p>
                          {quality && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${quality.cls} whitespace-nowrap`}>
                              {quality.label}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-1.5 flex-wrap">
                          <span className="flex items-center gap-1"><Mail size={10} /> {c.email}</span>
                          {c.city && <span className="flex items-center gap-1"><MapPin size={10} /> {c.city}</span>}
                        </div>
                        {c.categories?.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {c.categories.map(catSlug => {
                              const cat = CATEGORIES.find(x => x.value === catSlug);
                              if (!cat) return null;
                              return (
                                <span key={catSlug} className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${cat.soft}`}>
                                  <span className="w-1 h-1 rounded-full" style={{ background: cat.dot }} />
                                  {cat.label}
                                </span>
                              );
                            })}
                            {c.note && (
                              <span className="text-xs text-slate-400 italic ml-1 truncate max-w-xs">« {c.note} »</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="hidden md:flex flex-col items-end text-xs flex-shrink-0">
                        {isNew ? (
                          <>
                            <span className="text-slate-400 italic">Jamais sollicité</span>
                          </>
                        ) : (
                          <>
                            <span className="font-bold text-slate-900">{c.mission_count} mission{c.mission_count > 1 ? 's' : ''}</span>
                            <span className={c.acceptance_rate >= 85 ? 'text-emerald-600' : c.acceptance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}>
                              {c.acceptance_rate}% acceptées
                            </span>
                            <span className="text-slate-400 text-[10px]">Dernière : {relativeDate(c.last_mission_at) || 'jamais'}</span>
                          </>
                        )}
                      </div>

                      {/* Quick actions */}
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(c)}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          title="Modifier"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(c.id, c.company_name)}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                          title="Supprimer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>

          {/* Couverture warning */}
          {summary?.missing_categories?.length > 0 && !filterCategory && (
            <div className="p-4 bg-amber-50 border-t border-amber-100 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-amber-900">Couverture incomplète</p>
                <p className="text-[11px] text-amber-700">
                  Aucun contact pour : {missingCategoriesLabels.join(', ')}. Ajoutez-en pour ne pas être bloqué en cas d'urgence.
                </p>
              </div>
              <button
                onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
                className="text-xs font-semibold text-amber-900 hover:underline whitespace-nowrap"
              >
                + Ajouter
              </button>
            </div>
          )}
        </Card>
      </div>
    </MainLayout>
  );
};

export default ContractorsPage;
